import { Suspense, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { RecalculatingProvider } from "@/contexts/RecalculatingContext";
import { PendingGuidesProvider } from "@/contexts/PendingGuidesContext";
import { VideoPlayerProvider } from "@/contexts/VideoPlayerContext";
import { AutomationSupportProvider } from "@/contexts/AutomationSupportContext";
import { ZapierAuthProvider } from "@/contexts/ZapierAuthContext";
import { OnlineStatusProvider } from "@/hooks/useOnlineStatus.tsx";
import { Spinner } from "@/components/ui/spinner";
import { FEATURES } from "@/config/features";
import { lazyRetry } from "@/lib/lazyRetry";
import { useVideoPlayer } from "@/contexts/VideoPlayerContext";
import { useAutomationSupport } from "@/contexts/AutomationSupportContext";

const NotFound = lazyRetry(() => import("./pages/NotFound"));
const TermsOfService = lazyRetry(() => import("./pages/TermsOfService"));
const DataProcessingAgreement = lazyRetry(
  () => import("./pages/legal/DataProcessingAgreement"),
);
const PrivacyPolicy = lazyRetry(() => import("./pages/legal/PrivacyPolicy"));
const DoNotSellOrShare = lazyRetry(
  () => import("./pages/legal/DoNotSellOrShare"),
);
const MfaVerify = lazyRetry(() => import("./pages/MfaVerify"));
const Onboarding = lazyRetry(() => import("./pages/Onboarding"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const ProcessCapture = lazyRetry(() => import("./pages/ProcessCapture"));
const SOPLibrary = lazyRetry(() => import("./pages/SOPLibrary"));
const KnowledgeHub = lazyRetry(() => import("./pages/KnowledgeHub"));
const Settings = lazyRetry(() => import("./pages/Settings"));
const AutomationGuides = lazyRetry(() => import("./pages/AutomationGuides"));
const PurchasedAutomations = lazyRetry(
  () => import("./pages/PurchasedAutomations"),
);
const KnowledgeLibrary = lazyRetry(() => import("./pages/KnowledgeLibrary"));
const OAuthConsent = lazyRetry(() => import("./pages/OAuthConsent"));
const ContactSupport = lazyRetry(() => import("./pages/ContactSupport"));
const AdminDashboard = lazyRetry(() => import("./pages/AdminDashboard"));
const AppLayout = lazyRetry(() =>
  import("@/components/layout/AppLayout").then((module) => ({
    default: module.AppLayout,
  })),
);

const PersistentVideoPlayer = lazyRetry(() =>
  import("./components/video-player/PersistentVideoPlayer").then((module) => ({
    default: module.PersistentVideoPlayer,
  })),
);
const FloatingAutomationSupport = lazyRetry(() =>
  import("./components/automation-support/FloatingAutomationSupport").then(
    (module) => ({
      default: module.FloatingAutomationSupport,
    }),
  ),
);

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Spinner size={32} className="text-primary" />
  </div>
);

const MaybeZapierAuthProvider = ({ children }: { children: ReactNode }) => {
  if (FEATURES.ZAPIER_PARTNER_API) {
    return <ZapierAuthProvider>{children}</ZapierAuthProvider>;
  }
  return <>{children}</>;
};

const queryClient = new QueryClient();

const PersistentVideoPlayerGate = () => {
  const { currentVideo } = useVideoPlayer();
  if (!currentVideo) return null;
  return (
    <Suspense fallback={null}>
      <PersistentVideoPlayer />
    </Suspense>
  );
};

const FloatingAutomationSupportGate = () => {
  const { isOpen, selectedGuide } = useAutomationSupport();
  if (!isOpen || !selectedGuide) return null;
  return (
    <Suspense fallback={null}>
      <FloatingAutomationSupport />
    </Suspense>
  );
};

export default function AppShellRoutes() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RecalculatingProvider>
          <OnlineStatusProvider>
            <MaybeZapierAuthProvider>
              <PendingGuidesProvider>
                <VideoPlayerProvider>
                  <AutomationSupportProvider>
                    <PersistentVideoPlayerGate />
                    <FloatingAutomationSupportGate />
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route
                          path="/legal/dpa"
                          element={<DataProcessingAgreement />}
                        />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route
                          path="/legal/do-not-sell-or-share"
                          element={<DoNotSellOrShare />}
                        />
                        <Route path="/mfa-verify" element={<MfaVerify />} />
                        <Route
                          path="/oauth/consent"
                          element={<OAuthConsent />}
                        />
                        <Route
                          path="/onboarding"
                          element={
                            <ProtectedRoute requireOnboarding={false}>
                              <Onboarding />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/dashboard"
                          element={
                            <ProtectedRoute>
                              <RouteErrorBoundary routeName="Dashboard">
                                <Dashboard />
                              </RouteErrorBoundary>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/capture"
                          element={
                            <ProtectedRoute>
                              <RouteErrorBoundary routeName="ProcessCapture">
                                <ProcessCapture />
                              </RouteErrorBoundary>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/sops"
                          element={
                            FEATURES.SOP_LIBRARY ? (
                              <ProtectedRoute>
                                <RouteErrorBoundary routeName="SOPLibrary">
                                  <SOPLibrary />
                                </RouteErrorBoundary>
                              </ProtectedRoute>
                            ) : (
                              <Navigate to="/dashboard" replace />
                            )
                          }
                        />
                        <Route
                          path="/knowledge"
                          element={
                            <ProtectedRoute>
                              <RouteErrorBoundary routeName="KnowledgeHub">
                                <KnowledgeHub />
                              </RouteErrorBoundary>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute>
                              <RouteErrorBoundary routeName="Settings">
                                <Settings />
                              </RouteErrorBoundary>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/automation-guides"
                          element={
                            <ProtectedRoute>
                              <RouteErrorBoundary routeName="AutomationGuides">
                                <AutomationGuides />
                              </RouteErrorBoundary>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/purchased-automations"
                          element={
                            <ProtectedRoute>
                              <RouteErrorBoundary routeName="PurchasedAutomations">
                                <PurchasedAutomations />
                              </RouteErrorBoundary>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/tutorials"
                          element={
                            <ProtectedRoute>
                              <RouteErrorBoundary routeName="KnowledgeLibrary">
                                <KnowledgeLibrary />
                              </RouteErrorBoundary>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/contact-support"
                          element={
                            <ProtectedRoute>
                              <RouteErrorBoundary routeName="ContactSupport">
                                <ContactSupport />
                              </RouteErrorBoundary>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin"
                          element={
                            <ProtectedRoute>
                              <RouteErrorBoundary routeName="AdminDashboard">
                                <AppLayout>
                                  <AdminDashboard />
                                </AppLayout>
                              </RouteErrorBoundary>
                            </ProtectedRoute>
                          }
                        />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </AutomationSupportProvider>
                </VideoPlayerProvider>
              </PendingGuidesProvider>
            </MaybeZapierAuthProvider>
          </OnlineStatusProvider>
        </RecalculatingProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
